import { useCallback } from 'react';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel';
import { DateField, dateFieldLabels } from '../../Panels/BrowserPanel/utils/dateGrouping';
import { IconCheck } from 'components/Icons';
import './MobileSortOptionsPanel.scss';

/**
 * MobileSortOptionsPanel - Sort options panel for mobile To-Dos
 *
 * Displays a slide-up panel with date field options for sorting/grouping.
 *
 * @see Desktop equivalent: FiltersRow sort dropdown
 */

interface MobileSortOptionsPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Handler for closing the panel */
  onClose: () => void;
  /** Currently selected date field */
  dateField: DateField;
  /** Handler for date field change */
  onDateFieldChange: (field: DateField) => void;
}

/** Available date field options */
const DATE_FIELD_OPTIONS: DateField[] = ['lastMessage', 'dueDate', 'createdAt', 'transactionDate'];

export const MobileSortOptionsPanel = ({
  isOpen,
  onClose,
  dateField,
  onDateFieldChange,
}: MobileSortOptionsPanelProps) => {
  const prefix = prefixClass('ksq2-mobile-sort-options');

  const handleOptionClick = useCallback(
    (field: DateField) => {
      onDateFieldChange(field);
    },
    [onDateFieldChange]
  );

  return (
    <MobileSlideUpPanel isOpen={isOpen} onClose={onClose} title="Sort By">
      <div className={prefix()}>
        <div className={prefix('__options')}>
          {DATE_FIELD_OPTIONS.map(field => (
            <button
              key={field}
              type="button"
              className={classNames(prefix('__option'), {
                [prefix('__option--selected')]: field === dateField,
              })}
              onClick={() => handleOptionClick(field)}
            >
              <span className={prefix('__option-label')}>{dateFieldLabels[field]}</span>
              {field === dateField && (
                <span className={prefix('__option-check')}>
                  <IconCheck />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </MobileSlideUpPanel>
  );
};

export default MobileSortOptionsPanel;
